/**
 * The overlay focus ledger — **one** focus-return mechanism for every overlay kind
 * (parity card **P-260**, extending **P-013**).
 *
 * Until this round each overlay kind returned focus its own way and three of them could not:
 *
 *   1. a **menu** restored focus to `opener.focus()` synchronously inside its Escape handler,
 *      which the browser's own "the focused element was removed" handling then undid;
 *   2. a **submenu** restored focus to the parent row — a node its own Escape had just caused
 *      React to unmount, so focus landed on `<body>`;
 *   3. a **hoisted dialog** (the 10b/10e pattern: the menu closes and the dialog it chose opens
 *      in the same commit) captured `document.activeElement` in its mount effect, by which time
 *      the menu row had gone and `document.activeElement` was already `<body>`. It therefore had
 *      no opener at all, and Escape stranded the keyboard on the top of the document.
 *   4. and the one underneath all three: **`document.activeElement` is not the opener.** Vibe's
 *      `Button` — the board `···`, the workspace `···`, every trigger in the shell — cancels the
 *      mouse-down that would focus it, so a menu opened with the pointer captured `<body>` and
 *      had nothing to give back. The old code stored `<body>` too and called `body.focus()`,
 *      which is a no-op: that is why *every* dialog opened from the board menu landed on `<body>`
 *      and not only the ones the card names.
 *
 * Three rules fix all three, and every overlay primitive in the product goes through them:
 *
 *   - **the opener is captured, not guessed.** `captureOpener()` takes an explicit trigger when
 *     the surface knows one (`AnchoredPopover`'s anchor), otherwise `document.activeElement`,
 *     otherwise the opener the overlay that closed *in this same commit* handed over.
 *   - **an overlay that closes hands its opener on.** `handOffOpener()` publishes it for the
 *     length of one task — long enough for the dialog the menu just hoisted to claim it, short
 *     enough that the next unrelated click cannot.
 *   - **the restore happens after the paint that removed the overlay**, and only when focus has
 *     actually been lost (`<body>`, the document element, or a node no longer in the document).
 *     A dialog that autofocused its own field keeps it.
 *
 * A fourth rule makes nesting deterministic: every open overlay is on a **stack**, and only the
 * top one acts on Escape. The old arrangement leaned on listener registration order, which is
 * the opposite of what a capture-phase listener on `document` gives you — the *parent* menu's
 * listener runs first — and that is the whole of symptom 2.
 *
 * Nothing here traps focus or moves focus *into* an overlay: autofocus stays the surface's own
 * decision, exactly as in P-013.
 */
/** Controls a person opens an overlay *from*. Used to read the opener off a pointer event. */
const OPENER_SELECTOR = 'button, [role="button"], [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], ' +
    '[role="tab"], [role="option"], a[href], input, select, textarea, summary, [tabindex]';
/** A node that is worth focusing: still in the document, focusable, and not the body itself. */
export function isFocusableNode(node) {
    return (typeof HTMLElement !== 'undefined' &&
        node instanceof HTMLElement &&
        node.isConnected &&
        typeof node.focus === 'function' &&
        node !== document.body &&
        node !== document.documentElement);
}
/** `true` when focus is nowhere useful — which is the only state a restore may overwrite. */
export function focusIsLost() {
    if (typeof document === 'undefined')
        return false;
    const active = document.activeElement;
    return active === null || active === document.body || active === document.documentElement || !active.isConnected;
}
/**
 * How long an opener stays on offer. Long enough for a dialog raised **after an `await`** — the
 * `confirm()` behind *Archive board* and *Delete board* is a promise, so its dialog mounts in a
 * later task than the menu's unmount — and short enough to be a backstop only: the next pointer
 * gesture ends the hand-off outright, which is the real rule.
 */
const HANDOFF_MS = 1500;
let handOff = null;
let handOffTimer = null;
/**
 * The control the last pointer gesture went through — the opener, whether or not the browser
 * gave it focus. A capture-phase listener so it is recorded before any handler can move focus
 * or unmount the control, and installed once, lazily, by the first overlay that mounts.
 */
let lastPointerOpener = null;
let tracking = false;
/**
 * Installed when this module loads, **not** when the first overlay opens: the gesture that opens
 * the first menu of a page load happens before any overlay has mounted, so a lazily-installed
 * listener misses exactly the case it exists for. `@elizmonday/ui` is imported by the app's
 * entry point, so "module load" is "before the person can click anything".
 */
function trackOpeners() {
    if (tracking || typeof document === 'undefined')
        return;
    tracking = true;
    document.addEventListener('pointerdown', (event) => {
        /*
         * A new gesture also **ends** any hand-off in flight. The window is otherwise a timer, and
         * a timer is the wrong unit: what makes a menu's opener stale is somebody clicking
         * somewhere else, and that is exactly this event.
         */
        handOff = null;
        if (handOffTimer !== null)
            clearTimeout(handOffTimer);
        handOffTimer = null;
        const target = event.target;
        const node = target instanceof Element ? target.closest(OPENER_SELECTOR) : null;
        lastPointerOpener = isFocusableNode(node) ? node : null;
    }, true);
}
/**
 * An overlay is closing. Leave its opener where a dialog **hoisted out of it** can find it.
 *
 * The window is one task: React commits the menu's unmount and the dialog's mount in the same
 * synchronous commit, so the dialog's layout effect always sees this, and a `setTimeout(…, 0)`
 * always fires after it. A click somewhere else is a later task and finds nothing.
 *
 * **The first hand-off in that window wins.** A menu tree closes outside-in — React runs the
 * root's unmount cleanup before its flyout's — so the first opener published is the outermost
 * one, and that is the only one guaranteed to still be in the document afterwards. *Export board
 * to Excel* lives in a flyout, whose own opener is the `More actions ▸` row: last-wins handed the
 * dialog a row that was detached two lines later, `focus()` on it did nothing, and the sweep
 * found `<body>` again.
 */
export function handOffOpener(node) {
    if (handOff !== null)
        return;
    if (!isFocusableNode(node))
        return;
    handOff = { node, claimed: false };
    if (handOffTimer !== null)
        clearTimeout(handOffTimer);
    if (typeof setTimeout === 'function') {
        handOffTimer = setTimeout(() => {
            handOff = null;
            handOffTimer = null;
        }, HANDOFF_MS);
    }
}
/**
 * An overlay is closing, and whatever it claimed when it opened is over.
 *
 * Without this the guard below eats its own tail: a hoisted dialog *claims* the menu's trigger
 * when it opens, so on the way out `openerWasClaimed` was still true for that very trigger and
 * the dialog skipped its own restore — every dialog opened from the board menu landed on
 * `<body>` for exactly that reason. Releasing first makes the guard mean what it says: *another*
 * overlay, opening in this same commit, has taken the opener over.
 */
export function releaseOpener(node) {
    if (handOff === null || handOff.node !== node)
        return;
    handOff = null;
    if (handOffTimer !== null)
        clearTimeout(handOffTimer);
    handOffTimer = null;
}
/** `true` once another overlay has taken this opener over — the old owner must not restore it. */
export function openerWasClaimed(node) {
    return handOff !== null && handOff.node === node && handOff.claimed;
}
/**
 * The element an overlay opening **now** must return focus to on close.
 *
 * The hand-off is consulted **before** `document.activeElement` on purpose: a hoisted dialog
 * mounts in the commit that unmounted its menu, where the live `activeElement` is either
 * `<body>` or the menu row that is about to be detached — and the second is worse than the
 * first, because focusing a detached node silently does nothing.
 */
export function captureOpener(explicit) {
    trackOpeners();
    if (isFocusableNode(explicit))
        return explicit;
    if (handOff !== null && isFocusableNode(handOff.node)) {
        handOff.claimed = true;
        return handOff.node;
    }
    if (typeof document === 'undefined')
        return null;
    const active = document.activeElement;
    if (isFocusableNode(active))
        return active;
    /*
     * Last: the control the pointer went through. This is the case a focus-based capture cannot
     * see at all — a trigger that cancels the mouse-down that would have focused it — and it is
     * the majority of the shell's own `···` buttons.
     */
    return isFocusableNode(lastPointerOpener) ? lastPointerOpener : null;
}
/**
 * Put focus back on `node`, after the paint that removed the overlay.
 *
 * Restoring synchronously inside an unmount is undone by the browser's own handling of "the
 * focused element disappeared" — which is precisely what left focus on `<body>`. And a restore
 * that fires when focus has already moved somewhere real (a dialog's autofocused field, the
 * overlay that was hoisted out of this one) would be a steal, so it is skipped.
 */
export function returnFocusTo(node) {
    if (!isFocusableNode(node))
        return;
    const restore = () => {
        if (openerWasClaimed(node))
            return;
        if (!isFocusableNode(node) || !focusIsLost())
            return;
        node.focus();
    };
    if (typeof requestAnimationFrame === 'function')
        requestAnimationFrame(restore);
    else
        restore();
}
trackOpeners();
/* ── the stack ─────────────────────────────────────────────────────────────────────────── */
let stack = [];
export function pushOverlay(token) {
    stack = [...stack.filter((entry) => entry !== token), token];
}
export function popOverlay(token) {
    stack = stack.filter((entry) => entry !== token);
}
/** Only the innermost open overlay acts on Escape. */
export function isTopOverlay(token) {
    return stack.length > 0 && stack[stack.length - 1] === token;
}
/** Tests only: no overlay is open and nothing is mid-hand-off. */
export function __resetOverlayFocus() {
    stack = [];
    lastPointerOpener = null;
    handOff = null;
    if (handOffTimer !== null)
        clearTimeout(handOffTimer);
    handOffTimer = null;
}
//# sourceMappingURL=overlayFocus.js.map